import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase/config';
import { ref, onValue, off } from 'firebase/database';
import { toast } from 'react-hot-toast';

const useNotifications = () => {
  const { currentUser } = useAuth();
  const lastNotificationRef = useRef(Date.now());
  const isPageVisibleRef = useRef(true);

  useEffect(() => {
    // Track page visibility to avoid notifications when user is active
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    // Listen for new messages in all chat rooms where user is a participant
    const chatRoomsRef = ref(database, 'chatRooms');
    
    const unsubscribe = onValue(chatRoomsRef, (snapshot) => {
      const chatRoomsData = snapshot.val();
      if (!chatRoomsData) return;

      Object.entries(chatRoomsData).forEach(([roomId, roomData]) => {
        if (roomData.participants && roomData.participants[currentUser.uid]) {
          // Listen for new messages in this room
          const messagesRef = ref(database, `chatRooms/${roomId}/messages`);
          
          onValue(messagesRef, (messagesSnapshot) => {
            const messagesData = messagesSnapshot.val();
            if (!messagesData) return;

            // Check for new messages
            Object.entries(messagesData).forEach(([messageId, message]) => {
              // Only show notification if:
              // 1. Message is not from current user
              // 2. Message timestamp is after last notification check
              // 3. Page is not visible (user is not actively viewing)
              if (
                message.senderId !== currentUser.uid &&
                message.timestamp > lastNotificationRef.current &&
                !isPageVisibleRef.current
              ) {
                // Get sender name from room participants
                const otherUserId = Object.keys(roomData.participants).find(
                  id => id !== currentUser.uid
                );
                
                if (otherUserId) {
                  // Show notification
                  showNotification(message, roomId, otherUserId);
                }
              }
            });
          });
        }
      });

      // Update last notification timestamp
      lastNotificationRef.current = Date.now();
    });

    return () => off(chatRoomsRef, 'value', unsubscribe);
  }, [currentUser]);

  const showNotification = async (message, roomId, senderId) => {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        // Fallback to toast notification
        toast.success(`New message: ${message.text || 'File attachment'}`, {
          duration: 5000,
          onClick: () => {
            // Navigate to chat (you can implement this based on your routing)
            window.location.href = `/dashboard/chat?room=${roomId}`;
          }
        });
        return;
      }

      // Request permission if not granted
      if (Notification.permission === 'default') {
        await Notification.requestPermission();
      }

      if (Notification.permission === 'granted') {
        // Create browser notification
        const notification = new Notification('New Message', {
          body: message.text || 'You received a file attachment',
          icon: '/favicon.ico', // Replace with your app icon
          badge: '/favicon.ico',
          tag: `chat-${roomId}`, // Prevent duplicate notifications
          requireInteraction: false,
          silent: false
        });

        // Handle notification click
        notification.onclick = () => {
          window.focus();
          // Navigate to chat
          window.location.href = `/dashboard/chat?room=${roomId}`;
          notification.close();
        };

        // Auto close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);
      } else {
        // Fallback to toast if permission denied
        toast.success(`New message: ${message.text || 'File attachment'}`, {
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error showing notification:', error);
      // Fallback to toast
      toast.success(`New message: ${message.text || 'File attachment'}`, {
        duration: 5000
      });
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('This browser does not support notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast.success('Notifications enabled!');
        return true;
      } else {
        toast.error('Notifications permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Failed to enable notifications');
      return false;
    }
  };

  return {
    requestNotificationPermission,
    notificationPermission: typeof window !== 'undefined' && 'Notification' in window 
      ? Notification.permission 
      : 'default'
  };
};

export default useNotifications; 